import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, resolveComponent as _resolveComponent, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, renderSlot as _renderSlot, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, vShow as _vShow, withDirectives as _withDirectives, createSlots as _createSlots } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex items-center no-wrap"
}
const _hoisted_2 = {
  key: 1,
  class: "full-width flex items-center justify-center no-wrap relative-position"
}
const _hoisted_3 = {
  key: 2,
  class: "flex flex-center no-wrap relative-position"
}
const _hoisted_4 = {
  key: 4,
  class: "table-header-separator text-cool-gray-30 inline-block"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "flex items-center no-wrap" }
const _hoisted_7 = {
  key: 1,
  class: "LandingListTable__draggable-td"
}
const _hoisted_8 = { key: 2 }
const _hoisted_9 = {
  key: 3,
  class: "flex items-center justify-center"
}
const _hoisted_10 = {
  key: 4,
  class: "LandingListTable__row-action relative-position flex items-center justify-center"
}
const _hoisted_11 = {
  style: {"padding":"0 !important"},
  class: "full-height",
  colspan: "100%"
}

import { onBeforeUnmount, onMounted, Ref, ref, useAttrs, watch } from 'vue'
import defaultImage from 'src/shared/static/images/default-image.svg'
import translate from 'src/common/helpers/language/translate'
import globalTranslateObject from 'src/translate/global.translate'
import LandingListTableColumnLayout, {
  IColumnLayoutData,
  IDynamicTableColumn,
} from './LandingListTableColumnLayout.vue'
import { useGeneralStore } from 'src/stores'
import _ from 'lodash'
import type { ITableColumn, ITableRow } from 'src/common/interfaces'
import type { RouteLocationRaw } from 'vue-router'

interface IProps {
  columns: ITableColumn[]
  rows: T[]
  hasClickStyle?: boolean
  hasExport?: boolean
  draggable?: boolean
  clickStyleKey?: string
  selectedItem?: ITableRow
  maxColumnCount?: number
  defaultTableColumns?: string[]
  persistentColumns?: string[]
  layoutKey?: string
  noData?: INoData
  csvColumns?: ITableColumn[]
  csvName?: string
  allowRowSelection?: boolean
  allowRowDeselection?: boolean
  preselectedIds?: number[]
  hasKeyboardControl?: boolean
}

interface IEmits {
  (event: 'row-action', data: ITableRow): void
  (event: 'row-clicked', data: ITableRow): void
  (event: 'row-dragstart', data: ITableRow): void
  (event: 'row-dragover', data: ITableRow): void
  (event: 'row-dragend'): void
  (event: 'header-action'): void
  (event: 'empty-list-button:click'): void
  (event: 'column-reordered', data: ITableColumn[]): void
}

interface INoData {
  title: string
  description?: string
  illustration?: string | null
  icon?: string | null
  to?: RouteLocationRaw
  btnText?: string
  btnIcon?: string
  error?: boolean
  errorTitle?: string
  errorDescription?: string
  errorIllustration?: string
}


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'LandingListTable', inheritAttrs: false },
  __name: 'LandingListTable',
  props: /*@__PURE__*/_mergeModels({
    columns: {},
    rows: {},
    hasClickStyle: { type: Boolean, default: false },
    hasExport: { type: Boolean, default: true },
    draggable: { type: Boolean, default: false },
    clickStyleKey: { default: 'id' },
    selectedItem: { default: () => ({}) },
    maxColumnCount: { default: 10 },
    defaultTableColumns: { default: () => [] },
    persistentColumns: { default: () => [] },
    layoutKey: { default: '' },
    noData: { default: () => ({ title: '', description: '', illustration: '/images/illustration.png' }) },
    csvColumns: { default: () => [] },
    csvName: { default: '' },
    allowRowSelection: { type: Boolean, default: false },
    allowRowDeselection: { type: Boolean, default: false },
    preselectedIds: { default: () => [] },
    hasKeyboardControl: { type: Boolean, default: true }
  }, {
    "modelValue": { default: () => [] },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["row-action", "row-clicked", "row-dragstart", "row-dragover", "row-dragend", "header-action", "empty-list-button:click", "column-reordered"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {

const { t: globalT } = translate(globalTranslateObject)



const props = __props

const emit = __emit

const model = _useModel<ITableRow[]>(__props, "modelValue")

const attrs = useAttrs()

const generalStore = useGeneralStore()

const selectedRow: Ref<ITableRow> = ref({})

const hoveredRow: Ref<ITableRow> = ref({})

// List of columns passed to the k-table component
const columnsRef: Ref<ITableColumn[]> = ref([]) // Array of column objects

// List of columns names with a specific order passed to the layout component to render the columns
const columnsOrderRef: Ref<string[]> = ref([]) // Array of column names

// A history of column toggles that mimics a FIFO structure (Removes the oldest item when a new item is inserted)
const columnsToggleHistoryRef: Ref<string[]> = ref([]) // Array of column names

// List of columns that can be reordered/toggled in the layout menu (Fixed columns such as selection, id, ... are removed from it)
const dynamicColumnsRef: Ref<IDynamicTableColumn[]> = ref([]) // Array of column objects with id, isActive properties

// The default order of columns to be displayed
const defaultColumnsRef: Ref<ITableColumn[]> = ref([]) // Array of column objects

const columnsReorderLock: Ref<boolean> = ref(false)

function handleRowClick(row: ITableRow, e?: Event) {
  if (!(e?.target as Element)?.closest('.LandingListTable__row-action')) {
    selectedRow.value = row
    if (props.allowRowSelection && !shouldPreventRowDeselection(row)) {
      const index = _.findIndex(model.value, (selectedRow: ITableRow) => selectedRow.id === row.id)
      if (index !== -1) model.value.splice(index, 1)
      else model.value.push(row)
    }
    emit('row-clicked', row)
  }
}

function handleColumnReorder({ columns, order, toggleHistory, dynamicColumns }: IColumnLayoutData) {
  if (!columnsReorderLock.value) {
    columnsReorderLock.value = true
    columnsOrderRef.value = order
    columnsToggleHistoryRef.value = toggleHistory
    dynamicColumnsRef.value = dynamicColumns
    if (!defaultColumnsRef.value.length) defaultColumnsRef.value = _.cloneDeep(columns)
    columnsRef.value = _.cloneDeep(columns)
    emit('column-reordered', columnsRef.value)
    if (props.layoutKey) generalStore.tableColumnLayout[props.layoutKey] = order
    setTimeout(() => {
      columnsReorderLock.value = false
    }, 0)
  }
}

function generateDefaultColumns() {
  const fixedColumns = new Set(['actions', 'layout', 'id', 'selection'])
  let defaultColumns: string[] = []
  if (props.defaultTableColumns.length) defaultColumns = [...props.defaultTableColumns].splice(0, props.maxColumnCount)
  else {
    for (const column of props.columns) {
      if (!fixedColumns.has(column.name) && defaultColumns.length < props.maxColumnCount)
        defaultColumns.push(column.name)
    }
  }
  defaultColumnsRef.value = []
  for (const column of props.columns) {
    if (fixedColumns.has(column.name) || defaultColumns.includes(column.name)) defaultColumnsRef.value.push(column)
  }
}

function shouldPreventRowDeselection(row: ITableRow) {
  return props.preselectedIds.includes(row[String(attrs.rowKey ?? 'id')] as number) && !props.allowRowDeselection
}

async function handleKeyboardAction($event: KeyboardEvent) {
  if (!props.hasKeyboardControl) return

  const index = (props.rows as ITableRow[]).findIndex(
    (row) => row[props.clickStyleKey] === selectedRow.value[props.clickStyleKey],
  )

  setTimeout(() => {
    if ($event.key === 'ArrowUp' && index > 0 && props.rows[index - 1])
      handleRowClick(props.rows[index - 1] as ITableRow)
    else if ($event.key === 'ArrowDown' && index < props.rows.length - 1 && props.rows[index + 1])
      handleRowClick(props.rows[index + 1] as ITableRow)
  }, 0)
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyboardAction)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyboardAction)
})

watch(
  () => props.rows,
  (newRows) => {
    if (newRows.length) {
      selectedRow.value = props.selectedItem
    }
  },
  { immediate: true },
)

watch(
  () => props.selectedItem,
  () => {
    selectedRow.value = props.selectedItem
  },
)

watch(
  () => props.columns,
  (newColumns) => {
    const order = generalStore.tableColumnLayout[props.layoutKey]
    if (order) columnsOrderRef.value = order
    else if (props.defaultTableColumns?.length) columnsOrderRef.value = props.defaultTableColumns
    generateDefaultColumns()
    columnsRef.value = [...newColumns]
  },
  { immediate: true },
)

return (_ctx: any,_cache: any) => {
  const _component_k_tooltip = _resolveComponent("k-tooltip")!
  const _component_KCheckbox = _resolveComponent("KCheckbox")!
  const _component_k_icon = _resolveComponent("k-icon")!
  const _component_k_btn = _resolveComponent("k-btn")!
  const _component_k_callout = _resolveComponent("k-callout")!
  const _component_q_th = _resolveComponent("q-th")!
  const _component_q_tr = _resolveComponent("q-tr")!
  const _component_k_checkbox = _resolveComponent("k-checkbox")!
  const _component_KIcon = _resolveComponent("KIcon")!
  const _component_q_img = _resolveComponent("q-img")!
  const _component_q_td = _resolveComponent("q-td")!
  const _component_k_divider = _resolveComponent("k-divider")!
  const _component_EmptyListAlert = _resolveComponent("EmptyListAlert")!
  const _component_k_export_csv = _resolveComponent("k-export-csv")!
  const _component_k_table = _resolveComponent("k-table")!

  return (_openBlock(), _createBlock(_component_k_table, _mergeProps({
    selected: model.value,
    "onUpdate:selected": _cache[5] || (_cache[5] = ($event: any) => ((model).value = $event)),
    columns: columnsRef.value,
    rows: props.rows,
    class: "LandingListTable flex-1 flex column overflow-hidden",
    "root-class": "flex-1 flex column no-wrap overflow-hidden",
    "is-selectable": false,
    "pagination-disable": !__props.rows.length,
    "table-class": "hide-scrollbar"
  }, _ctx.$attrs), _createSlots({
    header: _withCtx(({ localProps }) => [
      _createVNode(_component_q_tr, { props: localProps }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localProps.cols, (col, index) => {
            return (_openBlock(), _createBlock(_component_q_th, {
              key: col,
              class: _normalizeClass(["relative-position fs-13px fw-500", [
            col.headerClasses,
            col.align ? `text-${col.align}` : 'text-left',
            __props.rows.length ? 'text-cool-gray-100' : 'no-pointer-events text-cool-gray-60',

            {
              'table-column-fit-content': ['actions', 'layout'].includes(col.name),
            },
          ]]),
              style: _normalizeStyle(col.style)
            }, {
              default: _withCtx(() => [
                (col.name === 'selection')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                      _createVNode(_component_KCheckbox, {
                        modelValue: localProps.selected,
                        "onUpdate:modelValue": ($event: any) => ((localProps.selected) = $event),
                        "checkbox-class": "q-mr-12",
                        dense: "",
                        color: "space-b-100"
                      }, {
                        "inner-checkbox": _withCtx(() => [
                          _createVNode(_component_k_tooltip, _mergeProps({
                            label: _unref(globalT)('select_all')
                          }, { ref_for: true }, col.tooltip), null, 16, ["label"])
                        ]),
                        _: 2
                      }, 1032, ["modelValue", "onUpdate:modelValue"]),
                      _createElementVNode("span", null, _toDisplayString(col.label), 1)
                    ]))
                  : (col.name === 'actions')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createVNode(_component_k_icon, {
                          class: _normalizeClass({ 'cursor-pointer': model.value.length }),
                          color: model.value.length ? 'cool-gray-100' : 'cool-gray-50',
                          name: "menu-dots",
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (model.value.length && _ctx.$emit('header-action')))
                        }, null, 8, ["class", "color"]),
                        (model.value.length)
                          ? (_openBlock(), _createBlock(_component_k_tooltip, _mergeProps({
                              key: 0,
                              label: _unref(globalT)('bulk_actions')
                            }, { ref_for: true }, col.tooltip), null, 16, ["label"]))
                          : _createCommentVNode("", true),
                        (model.value.length)
                          ? _renderSlot(_ctx.$slots, "header-action", {
                              key: 1,
                              localProps: localProps
                            })
                          : _createCommentVNode("", true)
                      ]))
                    : (col.name === 'layout')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                          _createVNode(_component_k_btn, {
                            model: "link",
                            "custom-color": "space-b-xd",
                            "icon-size": "20px",
                            icon: "table-layout",
                            class: "LandingListTable__layout-btn transition"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_k_tooltip, {
                                label: _unref(globalT)('column_layout')
                              }, null, 8, ["label"]),
                              _createVNode(LandingListTableColumnLayout, {
                                order: columnsOrderRef.value,
                                columns: __props.columns,
                                "toggle-history": columnsToggleHistoryRef.value,
                                "dynamic-columns": dynamicColumnsRef.value,
                                "default-columns": defaultColumnsRef.value,
                                "persistent-columns": __props.persistentColumns,
                                "max-col": __props.maxColumnCount,
                                onReorder: handleColumnReorder
                              }, null, 8, ["order", "columns", "toggle-history", "dynamic-columns", "default-columns", "persistent-columns", "max-col"])
                            ]),
                            _: 1
                          })
                        ]))
                      : (_openBlock(), _createElementBlock("span", {
                          key: 3,
                          class: _normalizeClass(['row items-center no-wrap gap-xs', col.labelClasses])
                        }, [
                          _createTextVNode(_toDisplayString(col.label) + " ", 1),
                          (col.tooltip)
                            ? (_openBlock(), _createBlock(_component_k_tooltip, _mergeProps({
                                key: 0,
                                ref_for: true
                              }, col.tooltip), null, 16))
                            : _createCommentVNode("", true),
                          (col.callout)
                            ? (_openBlock(), _createBlock(_component_k_callout, _mergeProps({
                                key: 1,
                                ref_for: true
                              }, col.callout), null, 16))
                            : _createCommentVNode("", true)
                        ], 2)),
                (index !== localProps.cols.length - 1 && (col.separator || col.separator === undefined))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4))
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1032, ["class", "style"]))
          }), 128))
        ]),
        _: 2
      }, 1032, ["props"])
    ]),
    body: _withCtx(({ localProps }: any) => [
      (localProps.pageIndex === 0)
        ? (_openBlock(), _createBlock(_component_q_tr, {
            key: 0,
            class: "LandingListTable__top-tr"
          }))
        : _createCommentVNode("", true),
      (props.rows.length)
        ? (_openBlock(), _createBlock(_component_q_tr, {
            key: 1,
            class: _normalizeClass(["LandingListTable__tr", {
          disable: !(localProps.row.is_active ?? true),
          'LandingListTable__tr--click-style':
            __props.hasClickStyle && localProps.row[__props.clickStyleKey] === selectedRow.value[__props.clickStyleKey],
        }]),
            props: localProps,
            draggable: __props.draggable,
            onMouseenter: ($event: any) => (hoveredRow.value = localProps.row),
            onMouseleave: _cache[2] || (_cache[2] = ($event: any) => (hoveredRow.value = {})),
            onClick: ($event: any) => (handleRowClick(localProps.row, $event)),
            onDragover: ($event: any) => (__props.draggable && _ctx.$emit('row-dragover', localProps.row)),
            onDragstart: ($event: any) => (__props.draggable && _ctx.$emit('row-dragstart', localProps.row)),
            onDragend: _cache[3] || (_cache[3] = ($event: any) => (__props.draggable && _ctx.$emit('row-dragend')))
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localProps.cols, (col) => {
                return (_openBlock(), _createBlock(_component_q_td, {
                  key: col.name,
                  props: localProps,
                  class: _normalizeClass(col.rowClasses),
                  onClick: ($event: any) => (col.emit && _ctx.$emit(col.emit, localProps.row))
                }, {
                  default: _withCtx(() => [
                    _renderSlot(_ctx.$slots, col.slot, {
                      localProps: localProps,
                      col: col
                    }, () => [
                      (col.name === 'selection')
                        ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
                            _createElementVNode("div", _hoisted_6, [
                              (__props.allowRowSelection)
                                ? (_openBlock(), _createBlock(_component_k_checkbox, {
                                    key: 0,
                                    "model-value": model.value,
                                    class: "q-mr-xs",
                                    value: localProps.row,
                                    disable: shouldPreventRowDeselection(localProps.row)
                                  }, null, 8, ["model-value", "value", "disable"]))
                                : (_openBlock(), _createBlock(_component_k_checkbox, {
                                    key: 1,
                                    modelValue: model.value,
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((model).value = $event)),
                                    class: "q-mr-xs",
                                    value: localProps.row,
                                    disable: shouldPreventRowDeselection(localProps.row)
                                  }, null, 8, ["modelValue", "value", "disable"])),
                              _createElementVNode("span", {
                                class: _normalizeClass(["fs-15px fw-400", [{ 'text-cool-gray-60': !(localProps.row.is_active ?? true) }, col.cellClasses]])
                              }, _toDisplayString(col.value), 3)
                            ])
                          ]))
                        : (col.draggable)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_7, [
                              _withDirectives(_createElementVNode("span", {
                                class: _normalizeClass(["fs-15px fw-400", [
                  {
                    'text-cool-gray-60': !(localProps.row.is_active ?? true),
                    'q-pl-xs': __props.draggable,
                  },
                  col.cellClasses,
                ]])
                              }, _toDisplayString(!col.ord ? col.value : localProps.pageIndex + 1), 3), [
                                [_vShow, !__props.draggable || hoveredRow.value.id !== localProps.row.id]
                              ]),
                              _withDirectives(_createVNode(_component_KIcon, {
                                name: "arrangement",
                                size: "24px",
                                color: "cool-gray-60",
                                class: "cursor-grab"
                              }, null, 512), [
                                [_vShow, __props.draggable && hoveredRow.value.id === localProps.row.id]
                              ])
                            ]))
                          : (col.type === 'image')
                            ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                                _createVNode(_component_q_img, {
                                  "no-spinner": "",
                                  "no-transition": "",
                                  class: _normalizeClass(['table-default-image', col.cellClasses]),
                                  alt: `${localProps.row.name} variant image`,
                                  src: col.value || _unref(defaultImage)
                                }, null, 8, ["class", "alt", "src"])
                              ]))
                            : (col.type === 'check')
                              ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
                                  _createVNode(_component_k_icon, {
                                    name: col.value ? 'tick' : 'remove',
                                    color: col.value ? 'success-100' : 'error-70'
                                  }, null, 8, ["name", "color"])
                                ]))
                              : (col.name === 'actions')
                                ? (_openBlock(), _createElementBlock("span", _hoisted_10, [
                                    _createVNode(_component_k_icon, {
                                      name: "menu-dots",
                                      color: !model.value.length ? 'cool-gray-70' : 'cool-gray-60',
                                      class: _normalizeClass({ 'cursor-pointer': !model.value.length }),
                                      onClick: ($event: any) => (!model.value.length && _ctx.$emit('row-action', localProps.row))
                                    }, null, 8, ["color", "class", "onClick"]),
                                    (!model.value.length)
                                      ? _renderSlot(_ctx.$slots, "row-action", {
                                          key: 0,
                                          localProps: localProps
                                        })
                                      : _createCommentVNode("", true)
                                  ]))
                                : (_openBlock(), _createElementBlock("span", {
                                    key: 5,
                                    class: _normalizeClass(["fs-15px fw-400", [{ 'text-cool-gray-60': !(localProps.row.is_active ?? true) }, col.cellClasses]])
                                  }, _toDisplayString(col.value), 3))
                    ])
                  ]),
                  _: 2
                }, 1032, ["props", "class", "onClick"]))
              }), 128))
            ]),
            _: 2
          }, 1032, ["class", "props", "draggable", "onMouseenter", "onClick", "onDragover", "onDragstart"]))
        : _createCommentVNode("", true),
      _createVNode(_component_q_tr, { class: "LandingListTable__bottom-tr" }, {
        default: _withCtx(() => [
          _createElementVNode("td", _hoisted_11, [
            _createVNode(_component_k_divider, {
              dotted: "",
              color: "cool-gray-50"
            })
          ])
        ]),
        _: 1
      })
    ]),
    "footer-action": _withCtx(() => [
      _renderSlot(_ctx.$slots, "footer-action", {}, () => [
        (__props.hasExport)
          ? (_openBlock(), _createBlock(_component_k_export_csv, {
              key: 0,
              size: "sm",
              rows: __props.rows,
              columns: __props.csvColumns?.length ? __props.csvColumns : __props.columns,
              "export-name": __props.csvName,
              disable: !__props.rows.length,
              disabled: !__props.rows.length
            }, null, 8, ["rows", "columns", "export-name", "disable", "disabled"]))
          : _createCommentVNode("", true)
      ])
    ]),
    pagination: _withCtx(() => [
      _renderSlot(_ctx.$slots, "pagination")
    ]),
    _: 2
  }, [
    (!_ctx.$attrs['loading'])
      ? {
          name: "no-data",
          fn: _withCtx(() => [
            _createVNode(_component_EmptyListAlert, _mergeProps({
              illustration: __props.noData.illustration ? __props.noData.illustration : __props.noData.icon ? '' : '/images/illustration.png'
            }, __props.noData, {
              "onClick:button": _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('empty-list-button:click')))
            }), null, 16, ["illustration"])
          ]),
          key: "0"
        }
      : undefined
  ]), 1040, ["selected", "columns", "rows", "pagination-disable"]))
}
}

})